import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { CHAT_DOCK_LAYOUT_ID, CHAT_DOCK_SPRING } from '@/common/motion/chatDockMotion';
import type { DockRect } from './chat-dock-geometry';

interface ChatDockFlightProps {
  /** Resting rect of the chat column (computeChatColumnRect). */
  rect: DockRect;
  mode: 'docking' | 'undocking';
  /** Undocking only: the flight landed back on the panel — settle to 'open'. */
  onArrived: () => void;
}

/**
 * The traveling "chat surface" of the dock/undock shared-element morph.
 *
 * Shares `layoutId` with HeaderChatLauncher: when this unmounts as the
 * launcher mounts (dock), or mounts as the launcher unmounts (undock),
 * framer-motion FLIPs the surface between the chat column rect and the
 * 34×34 header slot.
 *
 * Portaled to <body> with position:fixed so the flight renders above the
 * fixed app header (z-50) and is never clipped by the chat pane's
 * overflow/stacking contexts (.conversation-chat-panel is z-10 with
 * overflow:hidden — an in-grid traveler would vanish at the panel edge).
 * Purely visual: pointer-events none, aria-hidden.
 */
export function ChatDockFlight({ rect, mode, onArrived }: ChatDockFlightProps) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      className={`chat-dock-flight chat-dock-flight-${mode}`}
      layoutId={CHAT_DOCK_LAYOUT_ID}
      transition={{
        ...CHAT_DOCK_SPRING,
        // Fade-in (docking) syncs with the 50ms content fade beneath it —
        // a slower glass fade left ~70ms of half-ghost over an empty column.
        opacity: { duration: 0.06, ease: 'easeOut' },
      }}
      initial={mode === 'docking' ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      onLayoutAnimationComplete={mode === 'undocking' ? onArrived : undefined}
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        zIndex: 60,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    >
      {/* Glass lives on a child: WebKit can drop backdrop-filter when it
          shares an element with mask-image, so the mask stays on the
          traveler and the blur on this inner surface. */}
      <div className="chat-dock-flight-surface" />
    </motion.div>,
    document.body,
  );
}
